import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocalHospital as TreatmentIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import Axios from "axios";
import useUserState from "../../hooks/useUserState";
import useDentalAccess from "../../hooks/useDentalAccess";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(2, 0),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  statusChip: {
    fontWeight: "bold",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  dialogContent: {
    minWidth: 500,
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  priorityHigh: {
    backgroundColor: "#ffebee",
    color: "#c62828",
  },
  priorityMedium: {
    backgroundColor: "#fff3e0",
    color: "#ef6c00",
  },
  priorityLow: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
  },
}));

// Status y Priority mappings
const TREATMENT_STATUS = {
  pending: { label: "Pendiente", color: "default" },
  in_progress: { label: "En Progreso", color: "primary" },
  completed: { label: "Completado", color: "secondary" },
  cancelled: { label: "Cancelado", color: "error" },
};

const TREATMENT_PRIORITY = {
  low: { label: "Baja", class: "priorityLow" },
  medium: { label: "Media", class: "priorityMedium" },
  high: { label: "Alta", class: "priorityHigh" },
  urgent: { label: "Urgente", class: "priorityHigh" },
};

function DentalTreatments({ patientId, providerId, chartId, onRefresh }) {
  const classes = useStyles();
  const [userState] = useUserState();
  const dentalAccess = useDentalAccess();
  const { t } = useBDocTranslator();

  // Estados principales
  const [treatments, setTreatments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [formData, setFormData] = useState({
    procedure_id: "",
    tooth_number: "",
    description: "",
    status: "pending",
    priority: "medium",
    estimated_cost: "",
    notes: "",
    scheduled_date: "",
  });

  // Cargar datos iniciales
  useEffect(() => {
    if (patientId && dentalAccess.canView) {
      fetchTreatments();
      fetchProcedures();
    }
  }, [patientId, dentalAccess.canView]);

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/treatments/patient/${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${userState?.jwt}`,
          },
        }
      );
      setTreatments(response.data || []);
    } catch (error) {
      console.error("Error fetching treatments:", error);
      setTreatments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcedures = async () => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/procedures`,
        {
          headers: {
            Authorization: `Bearer ${userState?.jwt}`,
          },
        }
      );
      setProcedures(response.data || []);
    } catch (error) {
      console.error("Error fetching procedures:", error);
      setProcedures([]);
    }
  };

  const handleOpenDialog = (treatment = null) => {
    if (treatment) {
      setSelectedTreatment(treatment);
      setFormData({
        procedure_id: treatment.procedure_id || "",
        tooth_number: treatment.tooth_number || "",
        description: treatment.description || "",
        status: treatment.status || "pending",
        priority: treatment.priority || "medium",
        estimated_cost: treatment.estimated_cost || "",
        notes: treatment.notes || "",
        scheduled_date: treatment.scheduled_date
          ? format(parseISO(treatment.scheduled_date), "yyyy-MM-dd")
          : "",
      });
    } else {
      setSelectedTreatment(null);
      setFormData({
        procedure_id: "",
        tooth_number: "",
        description: "",
        status: "pending",
        priority: "medium",
        estimated_cost: "",
        notes: "",
        scheduled_date: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTreatment(null);
    setFormData({
      procedure_id: "",
      tooth_number: "",
      description: "",
      status: "pending",
      priority: "medium",
      estimated_cost: "",
      notes: "",
      scheduled_date: "",
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        patient_id: patientId,
        provider_id: providerId,
        chart_id: chartId,
        estimated_cost: formData.estimated_cost
          ? parseFloat(formData.estimated_cost)
          : null,
        scheduled_date: formData.scheduled_date || null,
      };

      if (selectedTreatment) {
        // Actualizar
        await Axios.put(
          `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/treatments/${selectedTreatment.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${userState?.jwt}`,
            },
          }
        );
      } else {
        // Crear
        await Axios.post(
          `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/treatments`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${userState?.jwt}`,
            },
          }
        );
      }

      handleCloseDialog();
      fetchTreatments();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error saving treatment:", error);
      // TODO: Mostrar mensaje de error al usuario
    }
  };

  const handleDelete = async (treatmentId) => {
    try {
      await Axios.delete(
        `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/treatments/${treatmentId}`,
        {
          headers: {
            Authorization: `Bearer ${userState?.jwt}`,
          },
        }
      );
      fetchTreatments();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error deleting treatment:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No programado";
    try {
      return format(parseISO(dateString), "dd/MM/yyyy", { locale: es });
    } catch {
      return "Fecha inválida";
    }
  };

  const getProcedureName = (procedureId) => {
    const procedure = procedures.find((p) => p.id === procedureId);
    return procedure?.name || "Procedimiento desconocido";
  };

  if (!dentalAccess.canView) {
    return (
      <Card className={classes.root}>
        <CardContent className={classes.emptyState}>
          <Typography variant="h6" color="error">
            {dentalAccess.errorMessage}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className={classes.root}>
        <CardContent className={classes.loadingContainer}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.root}>
      <CardContent>
        <div className={classes.header}>
          <Typography variant="h6" component="div">
            <TreatmentIcon
              style={{ marginRight: 8, verticalAlign: "middle" }}
            />
            {t("Tratamientos Dentales")}
          </Typography>
          {dentalAccess.canCreate && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t("Nuevo Tratamiento")}
            </Button>
          )}
        </div>

        {treatments.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body1">
              {t("No hay tratamientos registrados para este paciente")}
            </Typography>
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Procedimiento")}</TableCell>
                  <TableCell>{t("Diente")}</TableCell>
                  <TableCell>{t("Estado")}</TableCell>
                  <TableCell>{t("Prioridad")}</TableCell>
                  <TableCell>{t("Fecha Programada")}</TableCell>
                  <TableCell>{t("Costo Estimado")}</TableCell>
                  <TableCell>{t("Acciones")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {treatments.map((treatment) => (
                  <TableRow key={treatment.id}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontWeight: "bold" }}
                      >
                        {getProcedureName(treatment.procedure_id)}
                      </Typography>
                      {treatment.description && (
                        <Typography variant="caption" color="textSecondary">
                          {treatment.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{treatment.tooth_number || "N/A"}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          TREATMENT_STATUS[treatment.status]?.label ||
                          treatment.status
                        }
                        color={
                          TREATMENT_STATUS[treatment.status]?.color || "default"
                        }
                        size="small"
                        className={classes.statusChip}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          TREATMENT_PRIORITY[treatment.priority]?.label ||
                          treatment.priority
                        }
                        size="small"
                        className={
                          classes[
                            TREATMENT_PRIORITY[treatment.priority]?.class
                          ] || ""
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(treatment.scheduled_date)}
                    </TableCell>
                    <TableCell>
                      {treatment.estimated_cost
                        ? `$${treatment.estimated_cost.toFixed(2)}`
                        : "No estimado"}
                    </TableCell>
                    <TableCell>
                      <div className={classes.actionButtons}>
                        <Tooltip title={t("Ver detalles")}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(treatment)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {dentalAccess.canEdit && (
                          <Tooltip title={t("Editar")}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(treatment)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {dentalAccess.canDelete && (
                          <Tooltip title={t("Eliminar")}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDelete(treatment.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Dialog para crear/editar tratamiento */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTreatment ? t("Editar Tratamiento") : t("Nuevo Tratamiento")}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth className={classes.formRow}>
                <InputLabel>{t("Procedimiento")}</InputLabel>
                <Select
                  value={formData.procedure_id}
                  onChange={(e) =>
                    setFormData({ ...formData, procedure_id: e.target.value })
                  }
                >
                  {procedures.map((procedure) => (
                    <MenuItem key={procedure.id} value={procedure.id}>
                      {procedure.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Número de Diente")}
                value={formData.tooth_number}
                onChange={(e) =>
                  setFormData({ ...formData, tooth_number: e.target.value })
                }
                className={classes.formRow}
                placeholder="11, 12, 21, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("Descripción")}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={classes.formRow}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth className={classes.formRow}>
                <InputLabel>{t("Estado")}</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  {Object.entries(TREATMENT_STATUS).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth className={classes.formRow}>
                <InputLabel>{t("Prioridad")}</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                >
                  {Object.entries(TREATMENT_PRIORITY).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label={t("Fecha Programada")}
                value={formData.scheduled_date}
                onChange={(e) =>
                  setFormData({ ...formData, scheduled_date: e.target.value })
                }
                className={classes.formRow}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t("Costo Estimado")}
                value={formData.estimated_cost}
                onChange={(e) =>
                  setFormData({ ...formData, estimated_cost: e.target.value })
                }
                className={classes.formRow}
                InputProps={{ startAdornment: "$" }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("Notas Adicionales")}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                className={classes.formRow}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="default">
            {t("Cancelar")}
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            {selectedTreatment ? t("Actualizar") : t("Crear")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default DentalTreatments;
